import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Enums, Profile } from '@cyborg-monorepo/hunter/common';
import { AgentConfigOptions, ApmBase } from '@elastic/apm-rum';
import { ApmService } from '@elastic/apm-rum-angular';
import * as FullStory from '@fullstory/browser';
import { Analytics, AnalyticsInstance } from 'analytics';
import { filter, first } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { ChurnzeroConfig, churnzeroPlugin } from './churnzero.analytics';
import { hunterApiPlugin } from './hunter.analytics';

export interface TrackingServiceConfig {
  enabled: boolean;
  devMode?: boolean;
  trackers: {
    elastic: AgentConfigOptions;
    fullstory: Parameters<typeof FullStory.init>[0];
    churnzero: Omit<ChurnzeroConfig, 'httpClient'>;
  };
}

export interface TrackingAnalyticsIdentifyTraits {
  email: string;
  displayName: string;
  company: string;
  companyStatus: string;
  companyAEID: string;
  companyHuntModuleAccess: boolean;
}

export import TrackingEventEnum = Enums.TrackingEventEnum;

/**
 * Configure and initialize user tracking modules, and subscribe to any events to be tracked
 */
@Injectable()
export class TrackingService {
  private apm: ApmBase;
  private user: Profile;

  private analytics: AnalyticsInstance;

  // Stubbed here to allow taking in config from module symbol .forRoot pattern in the future, and not directly from environment
  private config: TrackingServiceConfig = environment.tracking;

  constructor(
    private router: Router,
    private apmService: ApmService,
    private authService: AuthService,
    private httpClient: HttpClient,
  ) {}

  public init(): void {
    if (this.config.enabled) {
      // After user is first authenticated, activate tracking modules
      this.authService.user$
        .pipe(
          filter((user) => !!user),
          first(),
        )
        .subscribe((user) => {
          if (user !== this.user) {
            this.user = user;
            this.initTrackingModules();
            this.identifyUser(user);
            this.subscribeToRouteChanges();
            this.trackEvent(TrackingEventEnum.AUTH_LOGIN);
          }
        });
    }
  }

  /**
   * Allow external event tracking across app
   */
  public trackEvent(
    eventName: TrackingEventEnum,
    eventMetadata?: object,
  ): void {
    if (this.config.enabled) {
      this.analytics.track(eventName, eventMetadata);
    }
  }

  /**
   * Start all tracking modules
   */
  private initTrackingModules(): void {
    const plugins = [
      hunterApiPlugin({
        httpClient: this.httpClient,
        hunterApiUrl: environment.api,
      }),
      /** 
       * Disabled until we resolve the updated Salesforce / CZ / Hunter App ID integration setup
      churnzeroPlugin({
        ...this.config.trackers.churnzero,
        httpClient: this.httpClient,
      }),
      */
    ];

    this.analytics = Analytics({
      app: 'hunter',
      // Only use internal hunter tracking in devMode
      plugins: this.config.devMode ? [plugins[0]] : plugins,
    });
    FullStory.init(this.config.trackers.fullstory);
    this.apm = this.apmService.init(this.config.trackers.elastic);
  }

  /**
   * Identify user across all tracking services
   */
  private identifyUser(user: Profile): void {
    // Namespace user IDs when in dev mode to avoid collisions and live account replacements
    const userId: string = this.config.devMode
      ? `DEV-${user.id.toString()}`
      : user.id.toString();
    const userDisplayName = `${user.firstName} ${user.lastName}`;

    const analyticsTraits: TrackingAnalyticsIdentifyTraits = {
      email: user.email,
      displayName: userDisplayName,
      company: user.company.name,
      companyStatus: user.company.status,
      companyAEID: user.company.accountExternalID,
      companyHuntModuleAccess: user.company.huntModuleAccess,
    };

    this.analytics.identify(userId, analyticsTraits);

    // Elastic APM
    this.apm.setUserContext({
      email: user.email,
      username: userDisplayName,
      id: userId,
    });
    this.apm.setCustomContext({
      company: user.company.name,
      companyStatus: user.company.status,
      companyAEID: user.company.accountExternalID,
      companyHuntModuleAccess: user.company.huntModuleAccess,
    });

    // Fullstory
    FullStory.identify(userId, analyticsTraits);
  }

  /**
   * Subscribe to and report on route / page changes
   */
  private subscribeToRouteChanges(): void {
    this.router.events
      .pipe(filter<NavigationEnd>((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.analytics.page();
      });
  }
}
